/**
 * TimeController - Manages system time polling and display updates
 * Handles timezone detection, format preferences, and battery optimization
 */

import { TimeController as TimeControllerInterface } from './interfaces/index.js';

/**
 * TimeController implementation with system time polling and battery optimization
 */
export class TimeController extends TimeControllerInterface {
  constructor(config = {}) {
    super();
    
    // Configuration with defaults
    this.config = {
      updateInterval: config.updateInterval || 1000, // 1 second default
      timeFormat: config.timeFormat || this.detectTimeFormat(),
      batteryOptimization: config.batteryOptimization !== false, // enabled by default
      ...config
    };
    
    // Internal state
    this.updateInterval = null;
    this.callbacks = [];
    this.isRunning = false;
    this.lastUpdateTime = null;
    this.timezone = this.detectTimezone();
    this.batteryLevel = null;
    this.isLowPowerMode = false;
    
    // Battery optimization setup
    this.setupBatteryOptimization();
    
    // Visibility change optimization
    this.setupVisibilityOptimization();
  }

  /**
   * Get the current system time with timezone information
   * @returns {Date} Current date and time
   */
  getCurrentTime() {
    return new Date();
  }

  /**
   * Start automatic time updates with 1-second precision
   * Implements battery optimization by adjusting update frequency
   */
  startTimeUpdates() {
    if (this.isRunning) {
      return; // Already running
    }
    
    this.isRunning = true;
    this.lastUpdateTime = this.getCurrentTime();
    
    // Determine update interval based on battery status
    const interval = this.getOptimalUpdateInterval();
    
    this.updateInterval = setInterval(() => {
      const now = this.getCurrentTime();
      this.lastUpdateTime = now;
      
      // Notify all registered callbacks
      this.callbacks.forEach(callback => {
        try {
          callback(now);
        } catch (error) {
          console.error('Error in time update callback:', error);
        }
      });
      
      // Periodically check if we need to adjust update frequency
      if (this.config.batteryOptimization) {
        this.adjustUpdateFrequencyIfNeeded();
      }
      
    }, interval);
    
    // Immediate update
    this.callbacks.forEach(callback => {
      try {
        callback(this.lastUpdateTime);
      } catch (error) {
        console.error('Error in time update callback:', error);
      }
    });
  }

  /**
   * Stop automatic time updates
   */
  stopTimeUpdates() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    this.isRunning = false;
  }

  /**
   * Register callback for time updates
   * @param {function(Date): void} callback - Callback function to call on time updates
   */
  onTimeUpdate(callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }
    this.callbacks.push(callback);
  }

  /**
   * Remove a callback from time updates
   * @param {function(Date): void} callback - Callback function to remove
   */
  removeTimeUpdateCallback(callback) {
    const index = this.callbacks.indexOf(callback);
    if (index > -1) {
      this.callbacks.splice(index, 1);
    }
  }

  /**
   * Get formatted time string according to user preferences
   * @param {Date} time - Time to format
   * @returns {string} Formatted time string
   */
  getFormattedTime(time = this.getCurrentTime()) {
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: this.config.timeFormat === '12h',
      timeZone: this.timezone
    };
    
    return time.toLocaleTimeString([], options);
  }

  /**
   * Get time format preference (12h or 24h)
   * @returns {'12h'|'24h'} Current time format
   */
  getTimeFormat() {
    return this.config.timeFormat;
  }

  /**
   * Set time format preference
   * @param {'12h'|'24h'} format - Time format to use
   */
  setTimeFormat(format) {
    if (format !== '12h' && format !== '24h') {
      throw new Error('Time format must be "12h" or "24h"');
    }
    this.config.timeFormat = format;
  }

  /**
   * Get current timezone
   * @returns {string} Timezone identifier
   */
  getTimezone() {
    return this.timezone;
  }

  /**
   * Get current update interval
   * @returns {number} Update interval in milliseconds
   */
  getUpdateInterval() {
    return this.config.updateInterval;
  }

  /**
   * Check if time updates are currently running
   * @returns {boolean} True if updates are running
   */
  isUpdating() {
    return this.isRunning;
  }

  /**
   * Get the last update time
   * @returns {Date|null} Last update time or null if never updated
   */
  getLastUpdateTime() {
    return this.lastUpdateTime;
  }

  // Private methods

  /**
   * Detect system timezone
   * @private
   * @returns {string} Detected timezone
   */
  detectTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      console.warn('Could not detect timezone, using UTC:', error);
      return 'UTC';
    }
  }

  /**
   * Detect system time format preference
   * @private
   * @returns {'12h'|'24h'} Detected time format
   */
  detectTimeFormat() {
    try {
      // Create a test time and see how it's formatted
      const testTime = new Date(2023, 0, 1, 13, 0, 0); // 1 PM
      const formatted = testTime.toLocaleTimeString();
      
      // If it contains AM/PM, it's 12-hour format
      return /AM|PM/i.test(formatted) ? '12h' : '24h';
    } catch (error) {
      console.warn('Could not detect time format, using 12h:', error);
      return '24h';
    }
  }

  /**
   * Setup battery optimization monitoring
   * @private
   */
  setupBatteryOptimization() {
    if (!this.config.batteryOptimization) {
      return;
    }

    // Battery API is experimental and may not be available
    if ('getBattery' in navigator) {
      navigator.getBattery().then(battery => {
        this.batteryLevel = battery.level;
        this.isLowPowerMode = battery.level < 0.2; // Consider low power when < 20%
        
        // Listen for battery changes
        battery.addEventListener('levelchange', () => {
          this.batteryLevel = battery.level;
          const wasLowPower = this.isLowPowerMode;
          this.isLowPowerMode = battery.level < 0.2;
          
          // If power mode changed and we're running, restart with new interval
          if (wasLowPower !== this.isLowPowerMode && this.isRunning) {
            this.restartWithOptimalInterval();
          }
        });
        
        battery.addEventListener('chargingchange', () => {
          // When charging starts, we can be more aggressive with updates
          if (battery.charging && this.isLowPowerMode && this.isRunning) {
            this.restartWithOptimalInterval();
          }
        });
      }).catch(error => {
        console.warn('Battery API not available:', error);
      });
    }
  }

  /**
   * Setup visibility change optimization
   * @private
   */
  setupVisibilityOptimization() {
    if (!this.config.batteryOptimization) {
      return;
    }

    document.addEventListener('visibilitychange', () => {
      if (this.isRunning) {
        if (document.hidden) {
          // App is hidden, reduce update frequency
          this.restartWithOptimalInterval();
        } else {
          // App is visible, restore normal frequency
          this.restartWithOptimalInterval();
        }
      }
    });
  }

  /**
   * Get optimal update interval based on current conditions
   * @private
   * @returns {number} Optimal update interval in milliseconds
   */
  getOptimalUpdateInterval() {
    let interval = this.config.updateInterval;
    
    if (!this.config.batteryOptimization) {
      return interval;
    }
    
    // Increase interval when app is hidden
    if (document.hidden) {
      interval *= 5; // Update every 5 seconds when hidden
    }
    
    // Increase interval when battery is low
    if (this.isLowPowerMode) {
      interval *= 2; // Update every 2 seconds when battery is low
    }
    
    // Cap the maximum interval at 10 seconds
    return Math.min(interval, 10000);
  }

  /**
   * Restart updates with optimal interval
   * @private
   */
  restartWithOptimalInterval() {
    if (this.isRunning) {
      this.stopTimeUpdates();
      this.startTimeUpdates();
    }
  }

  /**
   * Periodically adjust update frequency if needed
   * @private
   */
  adjustUpdateFrequencyIfNeeded() {
    const currentInterval = this.updateInterval ? 
      (this.updateInterval._idleTimeout || this.config.updateInterval) : 
      this.config.updateInterval;
    
    const optimalInterval = this.getOptimalUpdateInterval();
    
    // If optimal interval is significantly different, restart
    if (Math.abs(currentInterval - optimalInterval) > 500) {
      this.restartWithOptimalInterval();
    }
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.stopTimeUpdates();
    this.callbacks = [];
  }
}

export default TimeController;